/**
 * FabSpeedDial.js
 *
 * Плавающая кнопка «+» с раскрывающимся меню действий (Speed Dial).
 * Рендерится через createPortal → не зависит от overflow/stacking context родителя.
 *
 * Props:
 *   actions  — массив { icon: ReactNode, label: string, onClick: fn, color?: string }
 *   hidden   — принудительно скрыть (сверх общего механизма fab-hide/fab-show)
 *
 * ⚠️ Кнопка прячется САМА, когда её накрыл оверлей — см. hooks/useFabState:
 * там DOM-детект. Ручные события `fab-hide` / `fab-show` остались как ручка для
 * случаев, не видных по DOM. Так вышло потому, что опт-ин не выжил дважды:
 * сначала проп `hidden` передавала одна страница из девяти, потом события слали
 * 15 модалов из сотни. Решение о видимости живёт у кнопки, а не у каждого
 * модала, который об этом обязан помнить.
 *
 * `data-fab-root` на корне портала обязателен: собственное затемнение меню —
 * такой же `position:fixed; inset:0`, и без метки кнопка гасила бы сама себя.
 */
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { track } from '../analytics';
import { useFabState } from '../hooks/useFabState';
import { usePerm } from '../hooks/usePerm';
import { useHasTags } from '../hooks/useHasTags';
import ChannelFab from './channel/ChannelFab';
import { useChannel } from './channel/ChannelProvider';

/**
 * Сканер грузим лениво.
 *
 * ⚠️ FabSpeedDial живёт на десяти экранах и лежит в главном бандле. Статический
 * импорт затащил бы туда весь флоу скана — карточку оборудования, а за ней
 * форму Работы, — то есть сотни килобайт ради кнопки, которую на большинстве
 * заходов не нажимают. Первый запуск PWA на объектовом интернете это уже
 * оплачивал однажды.
 */
const EquipmentScanFlow = lazy(() => import('./EquipmentScanFlow'));

/** Рамка видоискателя — та же иконка, что у входов в сканер на других экранах. */
const SCAN_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8V5a2 2 0 0 1 2-2h3" /><path d="M16 3h3a2 2 0 0 1 2 2v3" />
    <path d="M21 16v3a2 2 0 0 1-2 2h-3" /><path d="M8 21H5a2 2 0 0 1-2-2v-3" />
    <line x1="7" y1="12" x2="17" y2="12" />
  </svg>
);

export default function FabSpeedDial({ actions = [], hidden = false }) {
  const { t } = useTranslation();
  const { can } = usePerm();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const { hidden: overlayHidden } = useFabState(btnRef);
  const channel = useChannel();
  // Нет выпущенных наклеек — сканировать нечего, пункт не показываем.
  const hasTags = useHasTags({ enabled: can('equipment.view') });
  const isHidden = hidden || overlayHidden;

  /**
   * «Сканировать QR» добавляет сама кнопка, а не каждая страница.
   *
   * ⚠️ Это ровно тот урок, что записан в шапке файла. Опт-ин здесь не выживет:
   * страниц с FAB десять, и любая новая про этот пункт забудет. Действие,
   * которое обязано быть ВЕЗДЕ, не может зависеть от того, вспомнил ли о нём
   * автор очередного экрана.
   *
   * Право `equipment.view` уже учитывает тумблер модуля на организации —
   * резолвер снимает права выключенных модулей, отдельной проверки не надо.
   */
  const allActions = can('equipment.view') && hasTags
    ? [...actions, {
        label: t('equipment.scanTag'),
        color: '#0f766e',
        icon: SCAN_ICON,
        onClick: () => { setScanning(true); track('equipment_scanner_opened', { source: 'fab' }); },
      }]
    : actions;

  // Закрыть при клике вне
  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onOutside);
    return () => document.removeEventListener('pointerdown', onOutside);
  }, [open]);

  // Схлопнуть меню, когда кнопку накрыло. Раскрытые пункты держат бэкдроп с
  // pointer-events, а невидимые перехватчики кликов — худший из возможных
  // багов: тап по модалу уходит в никуда.
  useEffect(() => {
    if (isHidden) setOpen(false);
  }, [isHidden]);

  return createPortal(
    <div
      ref={menuRef}
      data-fab-root=""
      style={{
        position:      'fixed',
        bottom:        20,
        right:         16,
        zIndex:        1000,
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'flex-end',
        gap:           10,
        opacity:       isHidden ? 0 : 1,
        pointerEvents: isHidden ? 'none' : 'auto',
        transition:    'opacity 0.2s',
      }}
    >
      {/* Action items — раскрываются вверх */}
      {open && allActions.map((action, i) => {
        const fire = () => {
          setOpen(false);
          track('fab_speed_dial_action_clicked', { label: action.label, source: 'label' });
          action.onClick();
        };
        return (
        <div
          key={i}
          style={{
            display:    'flex',
            alignItems: 'center',
            gap:        10,
            animation:  `fabItemIn 0.18s ease ${i * 0.04}s both`,
          }}
        >
          <span
            onClick={fire}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fire(); } }}
            style={{
            background:   'var(--bg)',
            color:        'var(--text)',
            fontSize:     13,
            fontWeight:   600,
            padding:      '5px 10px',
            borderRadius: 8,
            boxShadow:    '0 2px 8px rgba(0,0,0,0.18)',
            whiteSpace:   'nowrap',
            border:       '1px solid var(--border)',
            cursor:       'pointer',
            userSelect:   'none',
          }}>
            {action.label}
          </span>
          <button
            onClick={() => { setOpen(false); track('fab_speed_dial_action_clicked', { label: action.label, source: 'button' }); action.onClick(); }}
            aria-label={action.label}
            style={{
              width:          44,
              height:         44,
              borderRadius:   '50%',
              background:     action.color || 'var(--accent)',
              color:          '#fff',
              border:         'none',
              cursor:         'pointer',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              boxShadow:      `0 3px 12px ${action.color ? action.color + '55' : 'rgba(0,0,0,0.25)'}`,
              flexShrink:     0,
            }}
          >
            {action.icon}
          </button>
        </div>
        );
      })}

      {/* «Канал» — НАД «+» на всех экранах (решение Павла 02.10.2026). Пока
          меню «+» раскрыто, Канал прячем: на его месте встают пункты меню.
          Обёртка центрирует кнопку 44 px по оси «+» (48 px). */}
      {!open && channel && channel.enabled && (
        <div style={{ width: 48, display: 'flex', justifyContent: 'center' }}>
          <ChannelFab />
        </div>
      )}
      {/* Основная кнопка «+» / «✕» */}
      <button
        ref={btnRef}
        onClick={() => {
          setOpen(v => !v);
          track('fab_speed_dial_toggled', { open: !open, actions_count: allActions.length });
        }}
        aria-label={open ? t('common.close') : t('fab.menu')}
        style={{
          width:          48,
          height:         48,
          borderRadius:   '50%',
          background:     open ? 'var(--text3)' : 'var(--accent)',
          color:          '#fff',
          border:         'none',
          cursor:         'pointer',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          boxShadow:      '0 4px 16px rgba(0,0,0,0.28)',
          transition:     'background 0.2s, transform 0.2s',
          transform:      open ? 'rotate(45deg)' : 'rotate(0deg)',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5"  y1="12" x2="19" y2="12"/>
        </svg>
      </button>

      {/* Затемнение фона при открытом меню */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position:   'fixed',
            inset:      0,
            zIndex:     -1,
            background: 'rgba(0,0,0,0.25)',
          }}
        />
      )}

      {/* Сканер поверх всего. Отрисован ВНУТРИ портала кнопки, помеченного
          `data-fab-root`, — иначе автодетект оверлеев увидел бы поверх кнопки
          полноэкранное видео и погасил бы её вместе с открытым меню.

          Suspense без индикатора: чанк маленький, а спиннер на четверть
          секунды поверх экрана мигает хуже, чем его отсутствие. */}
      {scanning && (
        <Suspense fallback={null}>
          <EquipmentScanFlow
            onClose={() => setScanning(false)}
            // Своего Объекта у глобальной кнопки нет: экран может быть любой.
            // Флоу сам подставит Объект из сегодняшней Работы сканирующего.
            onOpenTask={(taskId, propId) => {
              setScanning(false);
              if (propId) navigate(`/properties/${propId}`, { state: { openTaskId: taskId } });
            }}
          />
        </Suspense>
      )}

      <style>{`
        @keyframes fabItemIn {
          from { opacity: 0; transform: translateY(8px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>,
    document.body
  );
}
